import { useRef, useState, useCallback, useEffect, type ReactNode } from 'react';

interface ScrollableNavProps {
  children: ReactNode;
  className?: string;
}

/**
 * Horizontally scrollable nav with fade indicators on edges.
 * Wraps children in a module-toolbar-button-group--scroll container.
 * Shows fade-left, fade-right, or fade-both when content overflows.
 */
export function ScrollableNav({ children, className = '' }: ScrollableNavProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [fade, setFade] = useState<'' | 'fade-left' | 'fade-right' | 'fade-both'>('');

  const updateFade = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const atLeft = el.scrollLeft <= 1;
    const atRight = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
    if (atLeft && atRight) setFade('');
    else if (atLeft) setFade('fade-right');
    else if (atRight) setFade('fade-left');
    else setFade('fade-both');
  }, []);

  useEffect(() => {
    updateFade();
    window.addEventListener('resize', updateFade);
    return () => window.removeEventListener('resize', updateFade);
  }, [updateFade]);

  return (
    <div
      ref={ref}
      className={`module-toolbar-button-group module-toolbar-button-group--scroll ${fade} ${className}`.trim()}
      onScroll={updateFade}
    >
      {children}
    </div>
  );
}
